import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import authFetch from '../../../utils/authFetch';

const apiUrl = import.meta.env.VITE_API_URL;

const RestaurantSettingsModal = ({ isOpen, onClose, onSave, initialSettings }) => {
  const [restaurantName, setRestaurantName] = useState('');
  const [restaurantSlogan, setRestaurantSlogan] = useState('');
  const [restaurantUrl, setRestaurantUrl] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [showStockAlert, setShowStockAlert] = useState(true);
  const [storeAddress, setStoreAddress] = useState('');
  const [openTime, setOpenTime] = useState('08:00');
  const [closeTime, setCloseTime] = useState('22:00');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && initialSettings) {
      setRestaurantName(initialSettings.restaurant_name || '');
      setRestaurantSlogan(initialSettings.restaurant_slogan || '');
      setRestaurantUrl(initialSettings.restaurant_url || '');
      setOwnerPhone(initialSettings.owner_phone || '');
      setShowStockAlert(initialSettings.show_stock_alert === 1 || initialSettings.show_stock_alert === true);
      setStoreAddress(initialSettings.store_address || '');
      setOpenTime(initialSettings.open_time || '08:00');
      setCloseTime(initialSettings.close_time || '22:00');
    }
  }, [isOpen, initialSettings]);

  const handleSave = async () => {
    // Validate required fields
    if (!restaurantName || !restaurantName.trim()) {
      setError('Nama Restoran wajib diisi.');
      return;
    }
    if (!restaurantSlogan || !restaurantSlogan.trim()) {
      setError('Slogan Restoran wajib diisi.');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      await onSave({
        restaurant_name: restaurantName.trim(),
        restaurant_slogan: restaurantSlogan.trim(),
        restaurant_url: restaurantUrl.trim() || 'www.berkah88.id',
        owner_phone: ownerPhone.trim() || null,
        show_stock_alert: showStockAlert ? 1 : 0,
        store_address: storeAddress.trim(),
        open_time: openTime.trim(),
        close_time: closeTime.trim()
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Gagal menyimpan pengaturan restoran.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1002] p-4">
      <div className="bg-white rounded-lg shadow-elevation-3 max-w-md w-full dark:bg-dark-card">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Store" size={24} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary">Pengaturan Restoran</h3>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <div className="space-y-4 mb-6 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
            <div>
              <label htmlFor="restaurant-name" className="block text-sm font-medium text-text-primary mb-1">
                Nama Restoran
              </label>
              <Input
                id="restaurant-name"
                type="text"
                value={restaurantName}
                onChange={(e) => setRestaurantName(e.target.value)}
                placeholder="Nama Restoran Anda"
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="restaurant-slogan" className="block text-sm font-medium text-text-primary mb-1">
                Slogan Restoran
              </label>
              <Input
                id="restaurant-slogan"
                type="text"
                value={restaurantSlogan}
                onChange={(e) => setRestaurantSlogan(e.target.value)}
                placeholder="Slogan Restoran Anda"
                className="w-full"
              />
            </div>
            <div>
              <label htmlFor="owner-phone" className="block text-sm font-medium text-text-primary mb-1">
                Nomor WhatsApp Owner
              </label>
              <Input
                id="owner-phone"
                type="text"
                value={ownerPhone}
                onChange={(e) => setOwnerPhone(e.target.value)}
                placeholder="Contoh: 08123456789"
                className="w-full"
              />
              <p className="text-[10px] text-text-secondary mt-1">Nomor ini digunakan untuk mengirim laporan stok kritis via WhatsApp.</p>
            </div>
            <div>
              <label htmlFor="restaurant-url" className="block text-sm font-medium text-text-primary mb-1">
                Website / URL Restoran (Tampil di Struk)
              </label>
              <Input
                id="restaurant-url"
                type="text"
                value={restaurantUrl}
                onChange={(e) => setRestaurantUrl(e.target.value)}
                placeholder="Contoh: www.berkah88.id"
                className="w-full"
              />
              <p className="text-[10px] text-text-secondary mt-1">Alamat ini akan ditampilkan di bagian paling bawah pada struk transaksi.</p>
            </div>

            <div>
              <label htmlFor="store-address" className="block text-sm font-medium text-text-primary mb-1">
                Alamat Fisik Toko
              </label>
              <textarea
                id="store-address"
                value={storeAddress}
                onChange={(e) => setStoreAddress(e.target.value)}
                placeholder="Contoh: Indramayu, Jawa Barat"
                className="w-full p-3 rounded-xl border border-border bg-background focus:ring-2 focus:ring-primary/20 outline-none transition-all text-sm min-h-[80px]"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="open-time" className="block text-sm font-medium text-text-primary mb-1">
                  Jam Buka
                </label>
                <Input
                  id="open-time"
                  type="text"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                  placeholder="08:00"
                  className="w-full"
                />
              </div>
              <div>
                <label htmlFor="close-time" className="block text-sm font-medium text-text-primary mb-1">
                  Jam Tutup
                </label>
                <Input
                  id="close-time"
                  type="text"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                  placeholder="22:00"
                  className="w-full"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <input
                id="show-stock-alert"
                type="checkbox"
                checked={showStockAlert}
                onChange={(e) => setShowStockAlert(e.target.checked)}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
              />
              <label htmlFor="show-stock-alert" className="text-sm font-medium text-text-primary cursor-pointer">
                Aktifkan Peringatan Stok Kritis di Dashboard
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={onClose} disabled={isLoading}>
              Batal
            </Button>
            <Button onClick={handleSave} disabled={isLoading}>
              {isLoading ? 'Menyimpan...' : 'Simpan Perubahan'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantSettingsModal;